import './DatePicker.scss';
import { useMemo, useState } from 'react';
import { getInputClassList, getInputWrapperClassList } from './DatePickerUtils/DatePickerUtils';
import DisplayDate from './DatePickerComponents/DisplayDate/DisplayDate';
import SvgIcon from '../SvgIcon/SvgIcon';
import DateDropdown from './DatePickerComponents/DateDropdown/DateDropdown';
import { getInputIconColour } from '../../utils/input/input';
import useToggle from '../../hooks/useToggle/useToggle';
import Text from '../Text/Text';

const DatePicker = ({
  inputId,
  label,
  initialPlaceholder = '',
  disabled = false,
  inactive = false,
  error = '',
  showTodayTomorrow = false,
  date,
  setDate,
  tabIndex = 0,
  dateFormat = 'long',
  showPastDates = false,
  hideRowGap = false,
}: {
  inputId: string;
  label: string;
  initialPlaceholder?: string;
  date?: Date;
  disabled?: boolean;
  inactive?: boolean;
  error?: string;
  showTodayTomorrow?: boolean;
  setDate: (v: Date) => void;
  tabIndex?: number;
  dateFormat?: 'long' | 'medium' | 'short';
  showPastDates?: boolean;
  hideRowGap?: boolean;
}) => {
  const day = date?.getDate();
  const month = date?.getMonth();
  const year = date?.getFullYear();

  // TODO: Convert to useDropdown for automatic close when clicking out
  const { value: isDropdownOpen, toggle: toggleDropdown } = useToggle(false);

  const [placeholder, setPlaceholder] = useState<string | undefined>(initialPlaceholder);
  const inputIconColour = useMemo(
    () => getInputIconColour(inactive, disabled),
    [inactive, disabled],
  );

  return (
    <div className={`${!hideRowGap && 'row-gap'} date-input-container`}>
      <label htmlFor={inputId} className="body-small">
        <Text type="body" size="small">
          {label}
        </Text>
      </label>
      <div
        className={getInputWrapperClassList(disabled, inactive, error, isDropdownOpen)}
        id={`${inputId}-date-select`}
        data-testid={`${inputId}-date-select`}
      >
        <button
          id={`${inputId}-select-button`}
          data-testid={`${inputId}-select-button`}
          className={getInputClassList(placeholder, error, isDropdownOpen, inactive)}
          type="button"
          role="combobox"
          aria-labelledby={inputId}
          aria-haspopup="listbox"
          aria-expanded={isDropdownOpen}
          aria-controls="select-dropdown"
          onClick={toggleDropdown}
          disabled={inactive || disabled}
          tabIndex={tabIndex}
        >
          <DisplayDate
            day={day}
            month={month}
            year={year}
            initialPlaceholder={initialPlaceholder}
            placeholder={placeholder}
            setPlaceholder={setPlaceholder}
            disabled={disabled}
            dateFormat={dateFormat}
          />
          <span>
            <SvgIcon iconName="Calendar" fill={inputIconColour} width={12} height={12} />
          </span>
        </button>
        {!inactive && !disabled ? (
          <DateDropdown
            date={date || new Date()}
            setDate={setDate}
            isDropdownOpen={isDropdownOpen}
            toggleDropdown={toggleDropdown}
            showTodayTomorrow={showTodayTomorrow}
            setPlaceholder={setPlaceholder}
            showPastDates={showPastDates}
          />
        ) : null}
      </div>
      {error && (
        <Text type="body" size="small" className="error-message">
          {error}
        </Text>
      )}
    </div>
  );
};

export default DatePicker;
